/// <reference path="../../../../node_modules/@1js/office-online-strings/en-US/transcription-strings.d.ts" />
import * as AudioBackup from "../../interfaces/IAudioBackup";
import * as AudioFormat from "../../interfaces/IAudioFormat";
import { CreateAndInitializeAudioFormat } from "../../audioBackup/AudioFormatFactory";
import { ITranscription } from "../../interfaces/ITranscriptionManager";
import { LogEvents } from "../../../utils/LogEvents";
import { LiveRecordingKeys } from "../../telemetry/LiveRecordingKeys";
import { LogError } from "../../../utils/ErrorTelemetry";
import { LogEventNames } from "../../interfaces/ITelemetryLogger";
import { DurationMeasureType } from "../../interfaces/ILogEvent";
import { IRealTimeTranscriptionConnection } from "../../interfaces/IRealTimeTranscriptionConnection";
import { GetDefaultTranscriptionLocale } from '../../../utils/TranscriptionLanguages';

export class LiveRecordingSession {
    private realTimeConnection: IRealTimeTranscriptionConnection;
    private audioBackup: AudioBackup.IAudioBackup | null;
    private audioFormat: AudioFormat.IAudioFormat | null;
    private recordedChunks: ArrayBuffer[] = [];
    private isAudioBackupEnabled: boolean =false;

    constructor(realTimeTranscriptionConnection: IRealTimeTranscriptionConnection,
        onError: (error: Error) => void,
        audioBackup: AudioBackup.IAudioBackup | null
    ) {
        this.audioBackup = audioBackup;

        if (audioBackup != null) { // we should check audioBackup for null if we are allowing Transcription to work with 3P cookies blocked.
          this.isAudioBackupEnabled = audioBackup.IsInitializedSuccessfully();
        }
        this.audioFormat = null;
        this.realTimeConnection = realTimeTranscriptionConnection;
        this.realTimeConnection.SetErrorCallback(onError);
        this.realTimeConnection.SetAudioBufferCallback((buffer: AudioBuffer) => this.OnAudioBufferCallback(buffer));
    }

    public async Initialize(): Promise<void> {
        const locale = GetDefaultTranscriptionLocale();
        LogEvents.LiveRecording.AddData([{key: LiveRecordingKeys.TranscriptionLocale, value: locale}]);
        LogEvents.LiveRecording.BeginDurationMeasure(DurationMeasureType.Normal, LiveRecordingKeys.ServiceInitializationDuration);
        await this.realTimeConnection.InitializeConnection(locale);
        LogEvents.LiveRecording.EndDurationMeasure(LiveRecordingKeys.ServiceInitializationDuration);
    }

    public async StartMicrophone(): Promise<void> {
        return this.realTimeConnection.StartMicrophone();
    }

    public async Pause(): Promise<void> {
        return this.realTimeConnection.PauseConnection();
    }

    public async Resume(): Promise<void> {
        return this.realTimeConnection.ResumeConnection();
    }

    public async GetRecording(): Promise<AudioFormat.IAudioFile> {
        return this.audioFormat!.EncodeFileUsingChunks(TranscriptionStrings.FileNameToUploadAudioFiles, this.recordedChunks);
    }

    public async GetTranscript(): Promise<ITranscription> {
        return this.realTimeConnection.EndSessionAndGetFinalTranscript();
    }

    public async Close(): Promise<void> {
        LogEvents.WavFormat.End();
        this.audioFormat = null;
        await this.realTimeConnection.PauseConnection();
        if (this.audioBackup != null) {
          this.audioBackup.Close();
        }
        return this.realTimeConnection.CloseConnection();
    }

    public ClearBackup() {
        if (this.audioBackup != null) {
          return this.audioBackup.ClearBackup();
        }
        return;
    }

    private async EncodeChunk(buffer: AudioBuffer) {
        if (this.audioFormat == null) {
            const formatProps: AudioFormat.IFormatProperties = {
                format: AudioFormat.SupportedFormats.wav,
                bitsPerSample: 16,
                sampleRateHz: buffer.sampleRate,
                countOfChannels: buffer.numberOfChannels,
                compressionPreferredKbps: 48
            };

            LogEvents.WavFormat.Start();
            this.audioFormat = CreateAndInitializeAudioFormat(formatProps);

            if (this.isAudioBackupEnabled && this.audioBackup != null) {
                try {
                    await this.audioBackup.BackupFormatProperties(formatProps);
                } catch (error: any) {
                    // If audio backup failed, disable audio backup
                    LogError(LogEventNames.IndexeddbBackup, "Audio backup failed: " + error.message);
                    this.isAudioBackupEnabled = false;
                }
            }
        }

        return this.audioFormat.EncodeChunk(buffer.getChannelData(0));
    }

    private async OnAudioBufferCallback(buffer: AudioBuffer) {
        LogEvents.LiveRecording.IncrementCount(LiveRecordingKeys.AudioChunksReceived);

        const encodedChunk = await this.EncodeChunk(buffer);
        this.recordedChunks.push(encodedChunk);

        if (!this.isAudioBackupEnabled)
            return;

        try {
          if(this.audioBackup != null) {
            await this.audioBackup.BackupChunk(encodedChunk);
          }
        } catch (error: any) {
            // If audio backup failed, disable audio backup
            LogError(LogEventNames.IndexeddbBackup, "Audio backup failed with error: " + error.message);
            this.isAudioBackupEnabled = false;
        }
    }
  };